import { ApostleFilterInput } from '@apeiron/apostle'
import { ActivityTypeData, ClipFilter } from '@apeiron/common'
import { styled } from '@mui/material'
import {
  ChipLabel,
  ChipToggleButton,
  ChipToggleButtonGroup
} from '@src/components/share/filter/ChipToggle'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle,
  FilterTitle
} from '@src/components/share/filter/FilterSection'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchActivityFilterJson from '@src/hooks/json/useFetchActivityFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const FILTER_FIELDS: (keyof ApostleFilterInput)[] = ['activityType']

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 10px 10px 10px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const Title = FilterTitle

const ChipGroup = styled(ChipToggleButtonGroup)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ChipButton = styled(ChipToggleButton)`
  padding: 8px 14px;
`

const ChipText = styled(ChipLabel)`
  font-size: 14px;
  margin: unset;
  text-align: center;
`

export const ActivityTypeFilterGroup = ({
  className,
  filterInput,
  onFilterChange,
  showTitle = true
}: ClassGroupProps) => {
  const [clipFilter, setClipFilter] = useState<ClipFilter<ActivityTypeData>>()

  const { data: filterSetting } = useFetchActivityFilterJson()

  useEffect(() => {
    if (R.isNotNil(filterSetting)) {
      setClipFilter(filterSetting.type)
    }
  }, [filterSetting])

  const handleOnChange = useCallback(
    (_: any, value: string) => {
      if (!R.isNil(clipFilter)) {
        onFilterChange(
          R.assoc<string, ApostleFilterInput, string>(
            clipFilter.field,
            value,
            filterInput
          )
        )
      }
    },
    [clipFilter, filterInput, onFilterChange]
  )

  return (
    <>
      {showTitle && <Title>{clipFilter?.name || ''}</Title>}
      <ChipGroup
        className={className}
        value={filterInput.activityType}
        onChange={handleOnChange}
      >
        {R.map((data: ActivityTypeData) => {
          return (
            <ChipButton key={data.type} value={data.type}>
              <ChipText>{data.name}</ChipText>
            </ChipButton>
          )
        }, clipFilter?.values || [])}
      </ChipGroup>
    </>
  )
}

const ApostleClassFilter: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const numberOfFilters = useMemo(() => {
    return R.isNotNil(filterInput.activityType)
      ? R.length(filterInput.activityType)
      : 0
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(R.omit(FILTER_FIELDS, filterInput))
  }, [filterInput, onFilterChange])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>{t('activity.filter.type')}</FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        <ActivityTypeFilterGroup
          className={className}
          filterInput={filterInput}
          onFilterChange={onFilterChange}
          showTitle={false}
        />
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: ApostleFilterInput
  onFilterChange: (input: ApostleFilterInput) => void
}

type ClassGroupProps = Props & {
  showTitle?: boolean
}

export default ApostleClassFilter
